// Small pieces shared by the event list cards (Events tab, a service's
// "What's on") and the event detail: long text with "More", the sitting's
// badges, and its set menu.
import { useId, useState, type ReactNode } from "react";
import type { WidgetEvent, WidgetEventSetMenu } from "../../types";
import { descriptionPreview, PREVIEW_CHARS } from "../../lib/description-preview";
import { menuBlocks } from "../../lib/menu-courses";

/** Text that shows a short preview (its first sentence, or about `budget`
 *  characters cut at a word with "…", lib/description-preview) and "More" for
 *  the rest, opened in place. The text itself is never changed. */
export function MoreText({
  text,
  className,
  about,
  budget = PREVIEW_CHARS,
  opened,
}: {
  text: string | null | undefined;
  className: string;
  /** What the text describes, for a screen reader: "More about <about>". */
  about: string;
  budget?: number;
  /** The whole text laid out (a menu's courses, MenuText) once "More" is
   *  pressed; absent = the text as written, in the same line. */
  opened?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const preview = descriptionPreview(text, budget);
  if (!preview) return null;
  const toggle = preview.more ? (
    <button
      type="button"
      className="vt-event-more"
      aria-expanded={open}
      aria-controls={id}
      onClick={(event) => {
        // Inside a card that has its own action: reading more never
        // does anything else.
        event.stopPropagation();
        setOpen((was) => !was);
      }}
    >
      {open ? "Less" : "More"}
      <span className="vt-option-desc-sr"> about {about}</span>
    </button>
  ) : null;
  if (open && opened) {
    return (
      <div className={className}>
        <div id={id}>{opened}</div>
        {toggle}
      </div>
    );
  }
  return (
    <p className={className}>
      <span id={id}>{open || !preview.more ? preview.full : preview.lead}</span>
      {toggle ? (
        <>
          {" "}
          {toggle}
        </>
      ) : null}
    </p>
  );
}

/** A menu's description as a menu: its text, and one row per course
 *  ("Starter" — "mussels with bruschetta"), lib/menu-courses. Text that is not
 *  written as courses shows as written. Every word is shown. */
export function MenuText({ text }: { text: string | null | undefined }) {
  const blocks = menuBlocks(text);
  if (!blocks) {
    return text?.trim() ? <p className="vt-menu-text">{text.trim()}</p> : null;
  }
  return (
    <>
      {blocks.map((block, i) =>
        block.kind === "text" ? (
          <p key={i} className="vt-menu-text">
            {block.text}
          </p>
        ) : (
          <dl key={i} className="vt-menu-courses">
            {block.courses.map((course, j) => (
              <div key={j} className="vt-menu-course">
                <dt className="vt-menu-course-label">{course.label}</dt>
                <dd className="vt-menu-course-dishes">{course.dishes}</dd>
              </div>
            ))}
          </dl>
        ),
      )}
    </>
  );
}

/** "Private sitting · one group" on a one-booking event, and "Booked" once
 *  another group has taken it. Nothing for an ordinary event. */
export function EventBadges({
  event,
  booked,
}: {
  event: Pick<WidgetEvent, "one_booking_only">;
  booked: boolean;
}) {
  if (!event.one_booking_only && !booked) return null;
  return (
    <div className="vt-event-badges">
      {event.one_booking_only && (
        <span className="vt-event-badge vt-event-badge-private">
          Private sitting · one group
        </span>
      )}
      {booked && <span className="vt-event-badge vt-event-badge-booked">Booked</span>}
    </div>
  );
}

/** The sitting's set menu: its name and its description with "More". */
export function SetMenuBlock({
  menu,
  compact = false,
}: {
  menu: WidgetEventSetMenu | null | undefined;
  /** A list card: a shorter preview. */
  compact?: boolean;
}) {
  if (!menu?.name) return null;
  return (
    <div className={`vt-event-set-menu${compact ? " vt-event-set-menu-compact" : ""}`}>
      <div className="vt-event-set-menu-label">Set menu</div>
      <div className="vt-event-set-menu-name">{menu.name}</div>
      <MoreText
        text={menu.description}
        className="vt-event-set-menu-desc"
        about={menu.name}
        budget={compact ? 70 : PREVIEW_CHARS}
      />
    </div>
  );
}

/** The event page's set menu: its name, then the whole menu (MenuText). The
 *  list cards use SetMenuBlock's short preview instead. */
export function SetMenuDetail({ menu }: { menu: WidgetEventSetMenu | null | undefined }) {
  if (!menu?.name) return null;
  return (
    <>
      <div className="vt-event-set-menu-name">{menu.name}</div>
      <MenuText text={menu.description} />
    </>
  );
}

/** The sitting's header image: the event's own, else its set menu's. */
export function eventHeroUrl(
  event: Pick<WidgetEvent, "hero_image_url" | "set_menu">,
): string | null {
  return event.hero_image_url || event.set_menu?.image_url || null;
}
